import { CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from './components/Auth/AuthContext/AuthContext.tsx';
import { setReauthHandler } from './utils/authBridge.ts';
import { AuthenticationGuard } from './components/Auth/AuthenticationGuard';
import FeatureFlagGuard from './components/Auth/FeatureFlagGuard';
import RoleGuard from './components/Auth/RoleGuard';
import { TourProvider } from './components/Tour/TourProvider.tsx';
import CaseWorkspaceLayout from './layouts/CaseWorkspaceLayout';
import MainLayout from './layouts/MainLayout';
import SettingsLayout from './layouts/SettingsLayout';
import SuperAdminLayout from './layouts/SuperAdminLayout';
import CaseDetails from './pages/CaseDetails';
import Dashboard from './pages/Dashboard';
import SimilarlySituatedBuilder from './pages/SimilarlySituatedBuilder';
import EndUserLicenseAgreement from './pages/EndUserLicenseAgreement';
import FeedbackPage from './pages/FeedbackPage.tsx';
import HelpCenter from './pages/HelpCenter';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import ResourceManager from './pages/ResourceManager.tsx';
import Team from './pages/Settings/Team';
import TeamManagement from './pages/SuperAdmin/TeamManagement.tsx';
import { Role } from './types/enums/Role.enum';
import ChangelogPage from './pages/ChangelogPage.tsx';
import UserManagement from './pages/SuperAdmin/UserManagement.tsx';
import ChangelogManagement from './pages/SuperAdmin/ChangelogManagement.tsx';
import FeatureFlagManagement from './pages/SuperAdmin/FeatureFlagManagement.tsx';
import JobTracker from './pages/SuperAdmin/JobTracker.tsx';

function App() {
  const { isLoading, loginWithRedirect } = useAuth();

  useEffect(() => {
    // Force logout on missing token
    setReauthHandler(() => {
      void loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    });
  }, [loginWithRedirect]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <TourProvider>
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route element={<AuthenticationGuard component={MainLayout} />}>
          <Route index path='/' element={<Dashboard />} />
          <Route path='resource-hub' element={<ResourceManager />} />
          <Route path='settings' element={<SettingsLayout />}>
            <Route index element={<Navigate to='team' replace />} />
            <Route
              path='team'
              element={<RoleGuard component={Team} requiredRoles={[Role.SUPER_ADMIN, Role.ADMIN]} />}
            />
          </Route>
          <Route element={<CaseWorkspaceLayout />}>
            <Route path='case-details/:caseId?' element={<CaseDetails />} />
            <Route
              path='similarly-situated-builder/:caseId?'
              element={
                <FeatureFlagGuard component={SimilarlySituatedBuilder} requiredFlag='SIMILARLY_SITUATED_BUILDER' />
              }
            />
          </Route>
          <Route
            path='super-admin'
            element={<RoleGuard component={SuperAdminLayout} requiredRoles={[Role.SUPER_ADMIN]} />}
          >
            <Route index element={<Navigate to='user-management' replace />} />
            <Route path='user-management' element={<UserManagement />} />
            <Route path='team-management' element={<TeamManagement />} />
            <Route path='changelog-management' element={<ChangelogManagement />} />
            <Route path='feature-flag-management' element={<FeatureFlagManagement />} />
            <Route path='job-tracker' element={<JobTracker />} />
          </Route>
          <Route path='help-center' element={<HelpCenter />} />
          <Route path='feedback' element={<FeedbackPage />} />
          <Route path='changelog' element={<ChangelogPage />} />
          <Route path='eula' element={<EndUserLicenseAgreement />} />
          <Route path='*' element={<NotFound />} />
        </Route>
      </Routes>
    </TourProvider>
  );
}

export default App;
